import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Flex,
  Grid,
  Radio,
  Select,
  Switch,
  Text,
  TextInput,
  useMantineColorScheme,
} from "@mantine/core";
import { IconCaretDownFilled } from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_GlobalFilterTextInput,
  useMantineReactTable,
} from "mantine-react-table";
import "react-datepicker/dist/react-datepicker.css";
import { DateTimePicker } from '@mantine/dates';
import DailyTimeRangeSelector from './DailyTimeRangeSelector.jsx';
import WeeklyStackedSingleRangeSelector from './WeeklyStackedSingleRangeSelector.jsx';
import { formatDate, generateDateRanges, generateWeeklySingleRangeOccurrences, parseOutageTimestamp, toLocalDisplayDateFromUtc, toUtcWithSameClock } from "./outageLoad.constants";
import YearlyTimelineChart from './YearlyTimelineChart.jsx';
import { generateRandomPeriods } from './randomEventsUtils.js';
import "./outageLoadManagement.scss";
import PropTypes from "prop-types";
import startDateAtom from "../../../../utils/atoms/startDateAtom";
import { useAtom } from "jotai";
import { formatInputValue, responsiveCol, isSameDisplayDateTimeToMinute } from "../../../../constants/helper.js";
import displayError from "../../../../constants/utils.js";
import {
  END_OF_DAY_HOUR_UTC,
  END_OF_DAY_MINUTE_UTC,
  END_OF_DAY_SECOND_UTC,
  END_OF_DAY_MILLISECOND_UTC,
  useProjectDateFormat,
} from "../../../../utils/helpers";
import { helpAtom } from "../../../../utils/atoms/helpAtom.js";
import { useHelpFocusBlur } from "../../../../utils/useHelpFocusBlur.js";
import { projectDetailsAtom } from "../../../../utils/atoms/projectDetailsAtom.js";
import ProjectNumberInput from "../../../common/ProjectNumberInput";
import moment from "moment";

const OutageLoadManagement = forwardRef(({ form, handleInputChange, gridParameters, getFieldAttributes }, ref) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [outageType, setOutageType] = useState("");
  const { colorScheme } = useMantineColorScheme();
  const isLightMode = colorScheme === "light";
  const [startDateAtomData] = useAtom(startDateAtom);
  const projectDateFormat = useProjectDateFormat();
  const [projectDetails] = useAtom(projectDetailsAtom);
  const [outageOccurDurationType, setOutageOccurDurationType] =
    useState("fullBackUp");
  const startDate =
    form?.values?.componentAttributes?.gridParameters?.outageStartDate;
  let startDateError =
    form.errors["componentAttributes.gridParameters.outageStartDate"];

  const endDate =
    form?.values?.componentAttributes?.gridParameters?.outageEndDate;
  let endDateError =
    form.errors["componentAttributes.gridParameters.outageEndDate"];
  const previousYear = new Date(new Date(new Date().setDate(new Date().getDate() - 1)).setFullYear(new Date().getFullYear() - 1))
    
  const defaultStartDate = startDateAtomData
    ? new Date(startDateAtomData)
    : previousYear;
  const defaultEndDate = (() => {
    const d = new Date(defaultStartDate);
    d.setUTCFullYear(d.getUTCFullYear() + 1);
    d.setUTCDate(d.getUTCDate() - 1);
    d.setUTCHours(
      END_OF_DAY_HOUR_UTC,
      END_OF_DAY_MINUTE_UTC,
      END_OF_DAY_SECOND_UTC,
      END_OF_DAY_MILLISECOND_UTC
    );
    return d;
  })();

  const powerOutageCost =
    form?.values?.componentAttributes?.gridParameters?.powerOutageCost;
  const outageCostStartDate =
    form?.values?.componentAttributes?.gridParameters?.powerOutageCostStartDate|| defaultStartDate;
  const outageCostEndDate =
    form?.values?.componentAttributes?.gridParameters?.powerOutageCostEndDate || defaultEndDate;

  // Track daily selector minutes (start/end) when outageType==='eachDay'
  const [dailyMinutes, setDailyMinutes] = useState({ start: null, end: null });
  // Track weekly stacked single range locally (do not rely on gridParameters)
  const [weeklyRange, setWeeklyRange] = useState(null);
  const [dateRangeError, setDateRangeError] = useState("");
  const [costRowError, setCostRowError] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [uniqueRowAdded, setUniqueRowAdded] = useState(false);
  const [isAddingUniqueRow, setIsAddingUniqueRow] = useState(false);
  const [prevOutageType, setPrevOutageType] = useState(null);
  const [showUniqueOutageSection, setShowUniqueOutageSection] = useState(
    form?.values?.componentAttributes?.gridParameters?.outageEvent ===
      "mixedEvent"
  );
  const [isAddClicked, setIsAddClicked] = useState(false);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const containerRef = useRef(null);

  // Random Events State
  const [randomEvents, setRandomEvents] = useState([]);
  const [numRandomPeriods, setNumRandomPeriods] = useState("5");
  const [randomBaseDuration, setRandomBaseDuration] = useState("4");
  const [randomVariability, setRandomVariability] = useState("20");
  const [randomOnePerDay, setRandomOnePerDay] = useState(true);
  const [isGeneratingRandom, setIsGeneratingRandom] = useState(false);

  useHelpFocusBlur(containerRef, setHelperAtom, helperAtom);

  useImperativeHandle(ref, () => ({ getDateRangeError: () => dateRangeError }), [dateRangeError]);

  const handleAddTableRow = () => {
    setIsAddClicked(true);
    if (outageType === 'eachDay') {
      if (!dailyMinutes.start && dailyMinutes.start !== 0) return; // require selection
      const baseStart = new Date(startDateAtomData);
      const baseEnd = new Date(defaultEndDate);
      let endMinutesVal = dailyMinutes.end;
      if (endMinutesVal === 0) endMinutesVal = 1440; // interpret 0 end as full day
      const newRows = generateDateRanges('eachDay', baseStart, baseEnd, dailyMinutes.start, endMinutesVal, projectDateFormat);
      setRows(newRows);
      handleInputChange('componentAttributes', 'outageDates', newRows);
      return;
    }
    if (outageType === 'eachWeek') {
      // Use locally tracked weeklyRange from selector
      if (!weeklyRange) return; // nothing selected
      const baseStart = new Date(startDateAtomData);
      const newRows = generateWeeklySingleRangeOccurrences(baseStart, weeklyRange, 1, defaultEndDate, projectDateFormat);
      setRows(newRows);
      handleInputChange('componentAttributes', 'outageDates', newRows);
      return;
    }
    if (outageType && startDate && endDate) {
      const newRow = {
        startingTime: formatDate(startDate, projectDateFormat),
        endingTime: formatDate(endDate, projectDateFormat),
        startUtc: new Date(startDate).toISOString(),
        endUtc: new Date(endDate).toISOString(),
      };

      if (outageType === "noRepetition") {
        if (new Date(startDate) >= new Date(endDate)) {
          setDateRangeError(t("outage.errors.startBeforeEnd") || "Start date and time must be before end date and time");
          return;
        }
        setDateRangeError("");

        // Strip trailing seconds and milliseconds using moment to enforce pristine minute boundaries
        const newRowFrom = moment.utc(newRow.startUtc || newRow.startingTime).startOf('minute').valueOf();
        const newRowTo = moment.utc(newRow.endUtc || newRow.endingTime).startOf('minute').valueOf();

        const hasOverlap = rows?.some((row) => {
            const rowFrom = moment.utc(row.startUtc || row.startingTime).startOf('minute').valueOf();
            const rowTo = moment.utc(row.endUtc || row.endingTime).startOf('minute').valueOf();
            return newRowFrom < rowTo && rowFrom < newRowTo;
        });

        if (hasOverlap) {
          setDateRangeError(
            t("outage.errors.overlap") ||
              "This date range overlaps or duplicates an existing entry"
          );
          return;
        } else {
          const updatedRows = [...rows, newRow];
          setRows(updatedRows);
          handleInputChange("componentAttributes", "outageDates", updatedRows);
        }

        handleInputChange("componentAttributes", "outageEndDate", toUtcWithSameClock(pickerMaxEndDate));
      } else {
        const newRows = generateDateRanges(outageType, startDateAtomData, endDate, null, null, projectDateFormat);
        setRows(newRows);
        handleInputChange("componentAttributes", "outageDates", newRows);
      }
    }
  };

  const handleAddUniqueOutageRow = async () => {
    if (
      outageType !== "eachDay" &&
      outageType !== "eachWeek" &&
      outageType !== "mixedEvent" &&
      outageType !== "randomEvents"
    ) {
      handleAddTableRow();
      return;
    }

    if (!outageCostStartDate || !outageCostEndDate) {
      displayError(
        "error",
        t("outage.messages.selectCostDates")
      );
      return;
    }
    // Compare displayed date + HH:mm (ignore seconds/milliseconds) via helper
    if (isSameDisplayDateTimeToMinute(outageCostStartDate, outageCostEndDate)) {
      setCostRowError(
        t("outage.errors.startBeforeEnd") ||
          "Start date and time must be before end date and time"
      );
      return;
    }

    if (new Date(outageCostStartDate) >= new Date(outageCostEndDate)) {
      setCostRowError(
        t("outage.errors.startBeforeEnd") ||
          "Start date and time must be before end date and time"
      );
      return;
    }

    // show loader while we validate/add (yield so loader renders)
    setIsAddingUniqueRow(true);
    await new Promise((res) => setTimeout(res, 50));

    const newRow = {
      startingTime: formatDate(outageCostStartDate, projectDateFormat),
      endingTime: formatDate(outageCostEndDate, projectDateFormat),
      startUtc: new Date(outageCostStartDate).toISOString(),
      endUtc: new Date(outageCostEndDate).toISOString(),
    };

    // Strip trailing seconds and milliseconds using moment to enforce pristine minute boundaries
    const newFrom = moment.utc(newRow.startUtc || newRow.startingTime).startOf('minute').valueOf();
    const newTo = moment.utc(newRow.endUtc || newRow.endingTime).startOf('minute').valueOf();

    const hasOverlap = rows?.some((row) => {
      const rowFrom = moment.utc(row.startUtc || row.startingTime).startOf('minute').valueOf();
      const rowTo = moment.utc(row.endUtc || row.endingTime).startOf('minute').valueOf();
      return newFrom < rowTo && newTo > rowFrom;
    });

    if (hasOverlap) {
      setCostRowError(
        t("outage.errors.overlap") ||
          "This date range overlaps or duplicates an existing entry"
      );
      setIsAddingUniqueRow(false);
      return;
    }

    setCostRowError("");
    const updatedRows = [...rows, newRow].sort(
      (a, b) =>
        new Date(a.startUtc || a.startingTime).getTime() - new Date(b.startUtc || b.startingTime).getTime()
    );
    setRows(updatedRows);
    handleInputChange("componentAttributes", "outageDates", updatedRows);
    setUniqueRowAdded(true);
    // remember previous outage type so we can revert later
    setPrevOutageType(outageType);
    // update both form and local state so UI stays visible for mixedEvent
    handleInputChange("componentAttributes", "outageEvent", "mixedEvent");
    setOutageType("mixedEvent");

    setIsAddingUniqueRow(false);
    displayError("success", t("outage.messages.addSuccess") || "Outage added");
  };

  const handleClearUniqueOutageFields = () => {
    setCostRowError("");
      handleInputChange(
        "componentAttributes",
        "powerOutageCostStartDate",
        defaultStartDate
      );
      handleInputChange(
        "componentAttributes",
        "powerOutageCostEndDate",
        defaultEndDate
      );
  };

  const revertMixedEvent = () => {
    if (uniqueRowAdded) {
      setUniqueRowAdded(false);
      const restore = prevOutageType || "";
      handleInputChange("componentAttributes", "outageEvent", restore);
      setOutageType(restore);
      setPrevOutageType(null);
    }
  };

  const pickerStartDate = toLocalDisplayDateFromUtc(startDate);
  const pickerEndDate = toLocalDisplayDateFromUtc(endDate);
  const pickerDefaultStartDate = toLocalDisplayDateFromUtc(defaultStartDate);
  const pickerDefaultEndDate = toLocalDisplayDateFromUtc(defaultEndDate);
  const pickerCostStartDate = toLocalDisplayDateFromUtc(outageCostStartDate);
  const pickerCostEndDate = toLocalDisplayDateFromUtc(outageCostEndDate);
  // The timeline covers the project's 12-month window (start date + 1 year), not a calendar year
  const timelineStartUtcMs = (() => {
    const d = new Date(defaultStartDate);
    const base = Number.isNaN(d.getTime()) ? new Date() : d;
    return Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate());
  })();
  const timelineEndExclusiveUtcMs = (() => {
    const d = new Date(timelineStartUtcMs);
    return Date.UTC(d.getUTCFullYear() + 1, d.getUTCMonth(), d.getUTCDate());
  })();
  const timelineMaxDays = Math.round(
    (timelineEndExclusiveUtcMs - timelineStartUtcMs) / (24 * 60 * 60 * 1000)
  );
  const MAX_RANDOM_PERIODS_MULTI_DAY = Math.max(600, timelineMaxDays * 2);
  const MAX_RANDOM_DURATION_HOURS = 24;

  const pickerMaxEndDate = (() => { const d = new Date(pickerDefaultStartDate); d.setFullYear(d.getFullYear() + 1); d.setDate(d.getDate() - 1); d.setHours(23, 59, 0, 0); return d; })();

  const columns = useMemo(
    () => [
      { accessorKey: "startingTime", header: t("outage.table.startDateHeader"), size: "50%" },
      { accessorKey: "endingTime", header: t("outage.table.endDateHeader"), size: "50%" },
    ],
    [t]
  );

  const enableCheckbox = useMemo(
    () =>
      outageType === "noRepetition" ||
      outageType === "mixedEvent" ||
      outageType === "randomEvents" ||
      outageType === "",
    [outageType]
  );

  const tableData = useMantineReactTable({
    data: rows,
    columns,
    enableColumnFilters: true,
    initialState: {
      density: "xs",
      showGlobalFilter: true,
    },
    // Explicitly control visibility of the built-in row selection column
    state: {
      columnVisibility: {
        'mrt-row-select': enableCheckbox,
      },
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    positionGlobalFilter: "right",
    mantineSearchTextInputProps: {
      placeholder: t("outage.table.searchPlaceholder"),
      sx: { minWidth: "400px" },
    },
    enableRowSelection: enableCheckbox,
    mantineTopToolbarProps: {
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineBottomToolbarProps: {
      sx: {
        padding: "10px",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: false,
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    enableStickyHeader: rows.length > 10,
    mantineTableContainerProps: {
      sx: {
        maxHeight: rows.length > 10 ? "460px" : undefined,
        overflowY: rows.length > 10 ? "auto" : undefined,
      },
    },
    mantineTableBodyProps: {
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableBodyCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableHeadCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    renderTopToolbar: ({ table }) => {
      const handleDeleteRow = () => {
        const remainingRows = rows.filter(
          (row) =>
            !table
              .getSelectedRowModel()
              .flatRows.map((row) => row.original)
              .includes(row)
        );
        setRows(remainingRows);
        setRowSelection({});
        handleInputChange("componentAttributes", "outageDates", remainingRows);
        if (remainingRows.length === 0) {
          setCostRowError("");
          revertMixedEvent();
        }
      };

      const handleDeleteAll = () => {
        setRows([]);
        setRowSelection({});
        setDailyMinutes({ start: null, end: null });
        setWeeklyRange(null);
        handleInputChange("componentAttributes", "outageDates", []);
        setCostRowError("");
        revertMixedEvent();
      };

      return (
        <Flex p="md" justify="space-between">
          <Flex gap="xs">
            <MRT_GlobalFilterTextInput table={table} />
          </Flex>
          {(outageType === "noRepetition" || outageType === "mixedEvent" || outageType === "randomEvents") && (
            <Flex sx={{ gap: "8px" }}>
              <Button
                aria-label="outage-delete-selected"
                color="gray"
                disabled={table?.getSelectedRowModel()?.flatRows?.length === 0}
                onClick={handleDeleteRow}
                variant="filled"
              >
                {t("outage.button.deleteSelected")}
              </Button>
              {(outageType === "noRepetition" || outageType === "randomEvents") && <Button color="gray" aria-label="outage-delete-all" onClick={handleDeleteAll} variant="filled">
                {t("outage.button.deleteAll")}
              </Button>}
            </Flex>
          )}
        </Flex>
      );
    },
  });

  const gridParametersHandledChange = (value) => {
    if (rows?.length) {
      setRows([]);
      handleInputChange("componentAttributes", "outageDates", []);
    } else {
      setRows([]);
    }
    if (value === 'noRepetition' || value === 'eachDay' || value === 'eachWeek') {
      handleInputChange("componentAttributes", "outageStartDate", defaultStartDate);
      handleInputChange("componentAttributes", "outageEndDate", defaultEndDate);
    }
    setDailyMinutes({ start: null, end: null });
    setWeeklyRange(null);
    setUniqueRowAdded(false);
    setCostRowError("");
    setRandomEvents([]);
    setOutageType(value);
    setShowUniqueOutageSection(value === "mixedEvent");
    handleInputChange("componentAttributes", "outageEvent", value);
  };

  const convertRandomEventsToTableFormat = (randomEventsList) => {
    return randomEventsList.map((event) => {
      const dayNumber = Math.max(1, Math.min(timelineMaxDays, Number(event.dayOfYear) || 1));
      const baseDate = new Date(timelineStartUtcMs);
      baseDate.setUTCDate(baseDate.getUTCDate() + (dayNumber - 1));
      
      const startHour = Math.floor(event.startHour);
      const startMinute = Math.round((event.startHour - startHour) * 60);
      const startingTimeDate = new Date(baseDate);
      startingTimeDate.setUTCHours(startHour, startMinute, 0, 0);
      
      const durationMs = event.duration * 60 * 60 * 1000;
      const endingTimeDate = new Date(startingTimeDate.getTime() + durationMs);
      
      return {
        startingTime: formatDate(startingTimeDate, projectDateFormat),
        endingTime: formatDate(endingTimeDate, projectDateFormat),
        startUtc: startingTimeDate.toISOString(),
        endUtc: endingTimeDate.toISOString(),
        id: event.id
      };
    });
  };

  const convertTableFormatToChartFormat = (tableRows) => {
    const allEvents = [];
    const msPerDay = 24 * 60 * 60 * 1000;
    const msPerHour = 60 * 60 * 1000;
    const yearStartUtcMs = timelineStartUtcMs;
    const yearEndExclusiveUtcMs = timelineEndExclusiveUtcMs;
    
    tableRows.forEach((row) => {
      try {
        const startDate = new Date(row.startUtc || row.startingTime);
        const endDate = new Date(row.endUtc || row.endingTime);
        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
          return;
        }
        const effectiveStartDate = new Date(Math.max(startDate.getTime(), yearStartUtcMs));
        const effectiveEndDate = new Date(Math.min(endDate.getTime(), yearEndExclusiveUtcMs));

        if (effectiveEndDate.getTime() <= effectiveStartDate.getTime()) {
          return;
        }

        const nextMidnightUtc = (d) =>
          new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1, 0, 0, 0, 0));

        let segmentStart = new Date(effectiveStartDate);
        while (segmentStart.getTime() < effectiveEndDate.getTime()) {
          const dayStart = new Date(
            Date.UTC(
              segmentStart.getUTCFullYear(),
              segmentStart.getUTCMonth(),
              segmentStart.getUTCDate(),
              0,
              0,
              0,
              0
            )
          );
          const dayEnd = nextMidnightUtc(segmentStart);
          const segmentEnd = new Date(Math.min(dayEnd.getTime(), effectiveEndDate.getTime()));

          const dayOfYear = Math.floor((dayStart.getTime() - yearStartUtcMs) / msPerDay) + 1;
          const startHour = (segmentStart.getTime() - dayStart.getTime()) / msPerHour;
          const duration = (segmentEnd.getTime() - segmentStart.getTime()) / msPerHour;

          if (duration > 0) {
            allEvents.push({
              id: `${String(row.id || "row")}-${dayOfYear}-${allEvents.length}`,
              dayOfYear: Math.max(1, Math.min(timelineMaxDays, dayOfYear)),
              startHour: Math.max(0, Math.min(24, startHour)),
              duration: Math.max(0, Math.min(24, duration)),
            });
          }

          segmentStart = segmentEnd;
        }
      } catch (error) {
        console.error("Error converting table row to chart format:", row, error);
      }
    });
    
    return allEvents;
  };

  const randomValidation = useMemo(() => {
    const errors = {
      numPeriods: "",
      duration: "",
      variability: "",
    };

    const numPeriods = Number(numRandomPeriods);
    const baseDuration = Number(randomBaseDuration);
    const variability = Number(randomVariability);
    const maxPeriods = randomOnePerDay ? timelineMaxDays : MAX_RANDOM_PERIODS_MULTI_DAY;
    const yearCapacityHours = timelineMaxDays * 24;

    if (!Number.isFinite(numPeriods) || !Number.isInteger(numPeriods) || numPeriods < 1) {
      errors.numPeriods = t("outage.errors.validPeriods") || "Enter a whole number greater than 0";
    } else if (numPeriods > maxPeriods) {
      errors.numPeriods = randomOnePerDay
        ? `Maximum is ${timelineMaxDays} when one event per day is enabled`
        : `Maximum allowed is ${MAX_RANDOM_PERIODS_MULTI_DAY} to prevent UI freeze`;
    }

    if (!Number.isFinite(baseDuration) || baseDuration <= 0) {
      errors.duration = t("outage.errors.validDuration") || "Duration must be greater than 0";
    } else if (baseDuration > MAX_RANDOM_DURATION_HOURS) {
      errors.duration = `Duration cannot exceed ${MAX_RANDOM_DURATION_HOURS} hours`;
    }

    if (!Number.isFinite(variability) || variability < 0 || variability > 100) {
      errors.variability = t("outage.errors.validVariability") || "Variability must be between 0 and 100";
    }

    if (!errors.numPeriods && !errors.duration && !errors.variability && !randomOnePerDay) {
      const maxDurationWithVariability = Math.min(
        MAX_RANDOM_DURATION_HOURS,
        baseDuration * (1 + variability / 100)
      );
      const requestedHours = numPeriods * maxDurationWithVariability;
      if (requestedHours > yearCapacityHours) {
        errors.numPeriods =
          `Requested outages can overlap. Reduce periods/duration so total is within ${yearCapacityHours} hours/year`;
      }
    }

    return {
      errors,
      isValid: !errors.numPeriods && !errors.duration && !errors.variability,
      maxPeriods,
    };
  }, [
    MAX_RANDOM_PERIODS_MULTI_DAY,
    numRandomPeriods,
    randomBaseDuration,
    randomOnePerDay,
    randomVariability,
    t,
    timelineMaxDays,
  ]);

  const handleGenerateRandomEvents = () => {
    const numPeriods = Number(numRandomPeriods);
    const baseDuration = Number(randomBaseDuration);
    const variability = Number(randomVariability);

    if (!randomValidation.isValid) {
      const firstError = randomValidation.errors.numPeriods || randomValidation.errors.duration || randomValidation.errors.variability;
      displayError("error", firstError || "Please correct random event inputs");
      return;
    }

    setIsGeneratingRandom(true);
    try {
      const randomEventsList = generateRandomPeriods(
        numPeriods,
        baseDuration,
        variability,
        randomOnePerDay,
        timelineMaxDays
      );

      setRandomEvents(randomEventsList);
      const formattedEvents = convertRandomEventsToTableFormat(randomEventsList);
      setRows(formattedEvents);
      handleInputChange("componentAttributes", "outageDates", formattedEvents);
      displayError("success", t("outage.messages.randomGenerated") || `Generated ${formattedEvents.length} random outage events`);
    } catch (error) {
      displayError("error", error.message || "Failed to generate random events");
    } finally {
      setIsGeneratingRandom(false);
    }
  };

  const filterEndTime = useMemo(() => {
    return (time) => {
      if (!startDate) return true;
      const selectedDate = new Date(time);
      const isSameDate =
        selectedDate.getUTCDate() === startDate.getUTCDate() &&
        selectedDate.getUTCMonth() === startDate.getUTCMonth() &&
        selectedDate.getUTCFullYear() === startDate.getUTCFullYear();
      if (isSameDate) {
        const startTimePlusOneMinute = new Date(startDate.getTime() + 60000);
        return selectedDate >= startTimePlusOneMinute;
      }
      return true;
    };
  }, [startDate]);

  useEffect(() => {
    const rawDates = gridParameters.componentAttributes?.gridParameters?.outageDates;
    const updatedOutageDates = rawDates?.map((date) => {
      const startDate = parseOutageTimestamp(date?.startUtc ?? date?.startingTime);
      const endDate = parseOutageTimestamp(date?.endUtc ?? date?.endingTime);
      return {
        startingTime: startDate ? formatDate(startDate, projectDateFormat) : "",
        endingTime: endDate ? formatDate(endDate, projectDateFormat) : "",
        startUtc: startDate?.toISOString() ?? null,
        endUtc: endDate?.toISOString() ?? null,
        id: date?.id,
      };
    });

    if (updatedOutageDates?.length) {
      setRows(updatedOutageDates);
      // Reconstruct weeklyRange if outageEvent is eachWeek and we have at least one row
      const evt = form?.values?.componentAttributes?.gridParameters?.outageEvent;
      if (evt === 'eachWeek') {
        const first = updatedOutageDates.find((date) => date.startUtc && date.endUtc);
        if (first) {
          const startDateObj = new Date(first.startUtc);
          const endDateObj = new Date(first.endUtc);
          // Derive start day/minute
          const startDay = startDateObj.getUTCDay();
          const startMinute = startDateObj.getUTCHours() * 60 + startDateObj.getUTCMinutes();
          let endDay = endDateObj.getUTCDay();
          let endMinute = endDateObj.getUTCHours() * 60 + endDateObj.getUTCMinutes();
          // Detect wrap: if end occurs before start in week index space OR spans more than a week boundary
          const wrap = (() => {
            const absStart = startDay * 1440 + startMinute;
            const absEnd = endDay * 1440 + endMinute;
            if (absEnd < absStart) return true;
            // If span crosses more than 6 days treat as wrap safety
            const daySpan = (endDateObj - startDateObj) / (1000 * 60 * 60 * 24);
            return daySpan >= 6.99 && endDay <= startDay; // rough heuristic for selections ending next week start
          })();
          // If end is exactly midnight next day we convert to 1440 boundary
          if (endDateObj.getUTCHours() === 0 && endDateObj.getUTCMinutes() === 0) {
            // Check if this midnight represents endMinute 1440 of previous day
            const prev = new Date(endDateObj.getTime() - 1 * 60 * 1000);
            if (prev.getUTCDay() === endDay) {
              // Actually stayed same day -> treat normally
            } else {
              // We advanced a day due to 24:00 boundary representation; roll back one day to compute boundary
              const adjustDay = (endDay - 1 + 7) % 7;
              endMinute = 1440; // boundary end
              // maintain endDay as original weekly selection terminal day before midnight rollover
              // For boundary we interpret endDay as day where 24:00 occurred
              endDay = adjustDay;
            }
          }
          setWeeklyRange({ startDay, startMinute, endDay, endMinute, wrap });
        }
      }
      if (evt === 'eachDay') {
        const firstDay = updatedOutageDates.find((date) => date.startUtc && date.endUtc);
        if (firstDay) {
          const startObj = new Date(firstDay.startUtc);
          const endObj = new Date(firstDay.endUtc);
          const startMins = startObj.getUTCHours() * 60 + startObj.getUTCMinutes();
          let endMins = endObj.getUTCHours() * 60 + endObj.getUTCMinutes();
          if (endObj.getUTCDate() !== startObj.getUTCDate() && endObj.getUTCHours() === 0 && endObj.getUTCMinutes() === 0) {
            endMins = 1440; // full day boundary 24:00
          }
            setDailyMinutes({ start: startMins, end: endMins });
        }
      }
    }

    const evt = form?.values?.componentAttributes?.gridParameters?.outageEvent;
    if (evt) {
      setOutageType(evt);
      setShowUniqueOutageSection(evt === "mixedEvent");
    }
  }, [projectDateFormat]);

  // If start date not set, default it to the previous calendar year's start
  // useEffect(() => {
  //   if (!startDate) {
  //     // Initialize form value for outageStartDate to defaultStartDate
  //     handleInputChange("componentAttributes", "outageStartDate", defaultStartDate);
  //   }
  // }, []); // run once on mount

  // Convert Date -> minutes from midnight helper (15-min aligned externally)
  const dateToMinutes = (d) => {
    if (!d) return null;
    return d.getUTCHours() * 60 + d.getUTCMinutes();
  };

  // When outageType is eachDay we show DailyTimeRangeSelector for time-of-day selection
  // We keep the calendar date from startDate/endDate if present; otherwise use today.
  const handleDailyRangeCommit = (startMinutes, endMinutes) => {
    if (outageType !== 'eachDay') return;
    setDailyMinutes({ start: startMinutes, end: endMinutes });
  };

  const handleOutageOccurDurationTypeChange = (value) => {
    setOutageOccurDurationType(value);
    handleInputChange("uiAttributes", "outageOccurDuration.backup", value);
  };

  useEffect(() => {
    const outageOccurType =
      form?.values?.uiAttributes?.gridParameters?.outageOccurDuration?.backup;
    if (outageOccurType === "fullBackUp") {
      handleOutageOccurDurationTypeChange(outageOccurType);
      handleInputChange("uiAttributes", "outageOccurDuration.loadValue", 100);
    } else {
      handleOutageOccurDurationTypeChange(outageOccurType || "fullBackUp");
      handleInputChange(
        "uiAttributes",
        "outageOccurDuration.loadValue",
        form?.values?.uiAttributes?.gridParameters?.outageOccurDuration
          ?.loadValue || 100
      );
    }
  }, [outageOccurDurationType]);

  const outageOptions = useMemo(
    () => [
      {
        value: "noRepetition",
        label: t("outage.select.noRepetition"),
      },
      { value: "eachWeek", label: t("outage.select.eachWeek") },
      { value: "eachDay", label: t("outage.select.eachDay") },
      {
        value: "randomEvents",
        label: t("outage.select.randomEvents"),
      },
      {
        value: "mixedEvent",
        label: t("outage.select.mixedEvent"),
        disabled: true,
      },
    ],
    [t]
  );

  return (
    <div ref={containerRef}>
      <Text
        component="span"
        color={colorScheme === "dark" ? "#d5d7e0" : "black"}
        fw={700}
      >
        {t("outageLoadManagement")}
      </Text>
      {/* Vertical stacked layout for eachDay / eachWeek / randomEvents */}
      {outageType === "eachDay" ||
      outageType === "eachWeek" ||
      outageType === "noRepetition" || outageType === "mixedEvent" || outageType === "randomEvents" ? (
        <div
          className={`outage-inline-row ${
            outageType === "noRepetition" ? "no-repetition-inline" : ""
          }`}
        >
          <Grid style={{ flex: 1 }} gutter={"sm"} align="flex-end">
            <Grid.Col {...responsiveCol}>
              <Select
                {...getFieldAttributes("field-12")}
                data-id="field-12"
                rightSection={<IconCaretDownFilled size={18} />}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                label={t("outage.select.label")}
                data={outageOptions}
                withAsterisk
                placeholder={t("outage.select.placeholder")}
                value={
                  form?.values?.componentAttributes?.gridParameters
                    ?.outageEvent || ""
                }
                {...form.getInputProps(
                  "componentAttributes.gridParameters.outageEvent"
                )}
                onChange={(val) => {
                  gridParametersHandledChange(val);
                  setIsAddClicked(false);
                  handleClearUniqueOutageFields();
                }}
                withinPortal
              />
            </Grid.Col>
            {outageType === "noRepetition" && (
              <>
                <Grid.Col className="no-rep-start" {...responsiveCol}>
                  <DateTimePicker
                    key={`outage-start-${projectDateFormat}`}
                    {...getFieldAttributes("field-13")}
                    data-id="field-13"
                label={t("outage.startDate.label")}
                    value={pickerStartDate || null}
                    onChange={(value) => {
                      setDateRangeError("");
                      if (!value) {
                        handleInputChange(
                          "componentAttributes",
                          "outageStartDate",
                          null
                        );
                        return;
                      }

                      const clampedValue =
                        pickerDefaultStartDate && value < pickerDefaultStartDate
                          ? pickerDefaultStartDate
                          : value;

                      handleInputChange(
                        "componentAttributes",
                        "outageStartDate",
                        toUtcWithSameClock(clampedValue)
                      );
                    }}
                    withSeconds={false}
                    clearable
                    valueFormat={`${projectDateFormat} HH:mm`}
                    minDate={pickerDefaultStartDate}
                    maxDate={
                      pickerEndDate || pickerDefaultEndDate
                    }
                  />
                </Grid.Col>
                <Grid.Col className="no-rep-end" {...responsiveCol} style={{ position: 'relative' }}>
                  <DateTimePicker
                    key={`outage-end-${projectDateFormat}`}
                    {...getFieldAttributes("field-14")}
                    data-id="field-14"
                    label={t("outage.endDate.label")}
                    value={pickerEndDate || null}
                    onChange={(value) => {
                      setDateRangeError("");
                      handleInputChange(
                        "componentAttributes",
                        "outageEndDate",
                        toUtcWithSameClock(value)
                      );
                    }}
                    withSeconds={false}
                    clearable
                    valueFormat={`${projectDateFormat} HH:mm`}
                    // Bound end date to the previous calendar year and ensure it
                    // is not before the selected start date (or previous year start)
                    minDate={pickerStartDate || pickerDefaultStartDate}
                    maxDate={pickerMaxEndDate}
                    disabled={!startDate}
                  />
                  {dateRangeError && (
                    <Text size="xs" color="red" style={{ position: 'absolute', bottom: -18, left: '2%' }}>
                      {dateRangeError}
                    </Text>
                  )}
                </Grid.Col>
                <Grid.Col span={2} className="no-rep-add-btn">
                  <div className="add-btn-wrapper" style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <Button
                      aria-label="outage-add-no-repetition"
                      color="gray"
                      variant="filled"
                      onClick={handleAddTableRow}
                      disabled={
                        (outageType === "eachDay" &&
                          (dailyMinutes.start == null ||
                            dailyMinutes.end == null)) ||
                        (outageType === "eachWeek" && !weeklyRange) ||
                        (outageType === "noRepetition" &&
                          (!startDate || !endDate))
                      }
                    >
                      {t("outage.button.add")}
                    </Button>
                    <Button
                      aria-label="outage-clear-no-repetition"
                      color="gray"
                      variant="outline"
                      onClick={() => {
                        if (outageType === 'noRepetition') {
                          handleInputChange("componentAttributes", "outageStartDate", null);
                          handleInputChange("componentAttributes", "outageEndDate", null);
                          return;
                        }
                        setRows([]);
                        setDailyMinutes({ start: null, end: null });
                        setWeeklyRange(null);
                        handleInputChange("componentAttributes", "outageDates", []);
                      }}
                    >
                      {t("outage.button.clear")}
                    </Button>
                  </div>
                </Grid.Col>
              </>
            )}
          </Grid>
          {outageType === "eachDay" && (
            <div className="daily-range-wrapper">
              <h6>{t("outage.daily.heading")}</h6>
              <DailyTimeRangeSelector
                initialStartMinutes={dailyMinutes.start}
                initialEndMinutes={dailyMinutes.end}
                onRangeCommit={handleDailyRangeCommit}
              />
              <div className="outage-hint-text">
                {t("outage.daily.hint")}
              </div>
            </div>
          )}
          {outageType === "eachWeek" && (
            <div className="weekly-range-wrapper">
              <h6>{t("outage.weekly.heading")}</h6>
              <WeeklyStackedSingleRangeSelector
                initialRange={weeklyRange}
                onRangeChange={(range) => {
                  setWeeklyRange(range);
                }}
              />
              <div className="outage-hint-text">
                {t("outage.weekly.hint")}
              </div>
            </div>
          )}

          {outageType === "randomEvents" && (
            <div className="random-events-wrapper">
              <h6>{t("outage.randomEvents.heading")}</h6>
              <Text size="xs" c={colorScheme === "dark" ? "#a0a3b1" : "#6b7280"} style={{ marginBottom: 8 }}>
                (Note: Random Generator settings are temporary and are not saved to database.)
              </Text>
              <Grid style={{ flex: 1 }} gutter={"sm"} align="flex-start">
                <Grid.Col {...responsiveCol}>
                  <ProjectNumberInput
                    {...getFieldAttributes("field-18")}
                    data-id="field-18"
                    label={t("outage.randomEvents.numPeriods") || "Number of Offgrid Periods"}
                    type="number"
                    min="1"
                    max={randomValidation.maxPeriods}
                    value={numRandomPeriods}
                    onChange={(e) => setNumRandomPeriods(e.currentTarget.value)}
                    error={randomValidation.errors.numPeriods}
                  />
                </Grid.Col>
                <Grid.Col {...responsiveCol}>
                  <ProjectNumberInput
                    {...getFieldAttributes("field-19")}
                    data-id="field-19"
                    label={t("outage.randomEvents.duration") || "Duration (hours)"}
                    type="number"
                    min="0.5"
                    step="0.5"
                    max={MAX_RANDOM_DURATION_HOURS}
                    value={randomBaseDuration}
                    onChange={(e) => setRandomBaseDuration(e.currentTarget.value)}
                    error={randomValidation.errors.duration}
                  />
                </Grid.Col>
                <Grid.Col {...responsiveCol}>
                  <ProjectNumberInput
                    {...getFieldAttributes("field-20")}
                    data-id="field-20"
                    label={t("outage.randomEvents.variability") || "Duration Variability (%)"}
                    type="number"
                    min="0"
                    max="100"
                    value={randomVariability}
                    onChange={(e) => setRandomVariability(e.currentTarget.value)}
                    placeholder="20"
                    error={randomValidation.errors.variability}
                  />
                </Grid.Col>
                <Grid.Col {...responsiveCol}>
                  <Checkbox
                    label={t("outage.randomEvents.onePerDay") || "Only one event per day"}
                    checked={randomOnePerDay}
                    onChange={(event) => setRandomOnePerDay(event.currentTarget.checked)}
                    style={{ marginTop: "24px" }}
                  />
                </Grid.Col>
              </Grid>
              <div className="add-btn-wrapper" style={{ paddingTop: 15, display: "flex", gap: 8 }}>
                <Button
                  aria-label="outage-generate-random"
                  color="gray"
                  variant="filled"
                  onClick={handleGenerateRandomEvents}
                  loading={isGeneratingRandom}
                  disabled={!randomValidation.isValid}
                >
                  {t("outage.button.generate") || "Generate"}
                </Button>
                
                <Button
                  aria-label="outage-clear-random"
                  color="gray"
                  variant="outline"
                  onClick={() => {
                    setRows([]);
                    setRandomEvents([]);
                    handleInputChange("componentAttributes", "outageDates", []);
                  }}
                >
                  {t("outage.button.clear")}
                </Button>
              </div>
            </div>
          )}

          {outageType !== "noRepetition" && (outageType === "eachWeek" || outageType === "eachDay") && (
            <div className="add-btn-wrapper" style={{ paddingBottom: 4, display:'flex', gap:8 }}>
              <Button
                aria-label="outage-add-recurring"
                color="gray"
                variant="filled"
                onClick={() => handleAddTableRow()}
                disabled={
                  (outageType === "eachDay" &&
                    (dailyMinutes.start == null || dailyMinutes.end == null)) ||
                  (outageType === "eachWeek" && !weeklyRange) ||
                  (outageType === "noRepetition" && (!startDate || !endDate))
                }
              >
                {t("outage.button.add")}
              </Button>
              <Button
                aria-label="outage-clear-recurring"
                color="gray"
                variant="outline"
                onClick={() => {
                  setRows([]);
                  setDailyMinutes({ start: null, end: null });
                  setWeeklyRange(null);
                  if (outageType === 'noRepetition') {
                    handleInputChange("componentAttributes", "outageStartDate", null);
                    handleInputChange("componentAttributes", "outageEndDate", null);
                  }
                  handleInputChange("componentAttributes", "outageDates", []);
                  setCostRowError("");
                  revertMixedEvent();
                }}
              >
                {t("outage.button.clear")}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Grid gutter="md" className="mt-2 mb-2">
          <Grid.Col {...responsiveCol}>
            <Select
              {...getFieldAttributes("field-12")}
              data-id="field-12"
              rightSection={<IconCaretDownFilled size={18} />}
              rightSectionProps={{ style: { pointerEvents: "none" } }}
              label={t("outage.select.label")}
              data={outageOptions}
              withAsterisk
              placeholder={t("outage.select.placeholder")}
              value={
                form?.values?.componentAttributes?.gridParameters
                  ?.outageEvent || ""
              }
              {...form.getInputProps(
                "componentAttributes.gridParameters.outageEvent"
              )}
              onChange={gridParametersHandledChange}
              withinPortal
            />
          </Grid.Col>
          {/* {outageType !== "eachDay" && (
            <>
              <Grid.Col
                span={3}
                style={{ position: "relative" }}
                className={
                  colorScheme === "dark"
                    ? "outage-date-picker"
                    : "outage-date-picker-light"
                }
              >
                <Text>{"Start Date"}</Text>
                <DatePicker
                  key={`outage-legacy-start-${projectDateFormat}`}
                  selected={startDate}
                  onChange={(date) => {
                    handleInputChange(
                      "componentAttributes",
                      "outageStartDate",
                      date
                    );
                  }}
                  showTimeSelect
                  timeIntervals={1}
                  dateFormat={`${projectDateFormat} HH:mm`}
                  timeFormat="HH:mm"
                  name="outageStartDate"
                  {...form.getInputProps(
                    "componentAttributes.gridParameters.outageStartDate"
                  )}
                  className={`${startDateError ? "date-picker-error" : ""}`}
                  calendarClassName={isLightMode ? "" : "dark-datepicker"}
                />
                {startDateError && (
                  <Text color="red" size="sm">
                    {startDateError}
                  </Text>
                )}
              </Grid.Col>
              <Grid.Col
                span={3}
                style={{ position: "relative" }}
                className={
                  colorScheme === "dark"
                    ? "outage-date-picker"
                    : "outage-date-picker-light"
                }
              >
                <Text>{"End Date"}</Text>
                <DatePicker
                  key={`outage-legacy-end-${projectDateFormat}`}
                  selected={endDate}
                  onChange={(date) => {
                    handleInputChange(
                      "componentAttributes",
                      "outageEndDate",
                      date
                    );
                  }}
                  showTimeSelect
                  timeIntervals={1}
                  dateFormat={`${projectDateFormat} HH:mm`}
                  timeFormat="HH:mm"
                  minDate={startDate}
                  disabled={!startDate}
                  filterTime={filterEndTime}
                  name="outageEndDate"
                  {...form.getInputProps(
                    "componentAttributes.gridParameters.outageEndDate"
                  )}
                  className={`${endDateError ? "date-picker-error" : ""}`}
                  calendarClassName={isLightMode ? "" : "dark-datepicker"}
                />
                {endDateError && (
                  <Text color="red" size="sm">
                    {endDateError}
                  </Text>
                )}
              </Grid.Col>
            </>
          )} */}
          {/* Weekly previously used grid; now inline layout handles rendering */}
          <Grid.Col
            className={`${
              startDateError || endDateError
                ? "outage-error"
                : "outage-add-button"
            }`}
            {...responsiveCol}
          >
            {outageType && (
              <div style={{ display:'flex', gap:8 }}>
                <Button aria-label="outage-add-generic" color="gray" variant="filled" onClick={handleAddTableRow}>
                  {t("outage.button.add")}
                </Button>
                <Button
                  aria-label="outage-clear-generic"
                  color="gray"
                  variant="outline"
                  onClick={() => {
                    setRows([]);
                    setDailyMinutes({ start: null, end: null });
                    setWeeklyRange(null);
                    if (outageType === 'noRepetition') {
                      handleInputChange("componentAttributes", "outageStartDate", null);
                      handleInputChange("componentAttributes", "outageEndDate", null);
                    }
                    handleInputChange("componentAttributes", "outageDates", []);
                  }}
                >
                  {t("outage.button.clear")}
                </Button>
              </div>
            )}
          </Grid.Col>
        </Grid>
      )}
                {(isAddClicked || outageType === "mixedEvent" || outageType === "randomEvents") && outageType!== "noRepetition" && (
            <Grid>
              <Grid.Col style={{ alignContent: "flex-end" }} span={4}>
                <Switch
                  labelPosition="left"
                  label={t("outage.unique.heading")}
                  checked={showUniqueOutageSection}
                  color="seGreen.0"
                  onChange={(event) => {
                    const isChecked = event.currentTarget.checked;
                    setShowUniqueOutageSection(isChecked);
                    if (!isChecked) {
                      setCostRowError("");
                      handleInputChange(
                        "componentAttributes",
                        "powerOutageCostStartDate",
                        outageCostStartDate
                      );
                      handleInputChange(
                        "componentAttributes",
                        "powerOutageCostEndDate",
                        outageCostEndDate
                      );
                    }
                  }}
                />
              </Grid.Col>
            </Grid>
          )}
          {showUniqueOutageSection && (isAddClicked || outageType === "mixedEvent" || outageType === "randomEvents") && (
            <>
             <Grid style={{ flex: 1 }} gutter={"sm"} align="flex-end">
               <Grid.Col className="no-rep-start" {...responsiveCol}>
                  <DateTimePicker
                    key={`outage-cost-start-${projectDateFormat}`}
                    {...getFieldAttributes("field-13")}
                    data-id="field-13"
                label={t("outage.startDate.label")}
                    value={pickerCostStartDate || null}
                    onChange={(value) => {
                      setCostRowError("");
                      if (!value) {
                        handleInputChange(
                          "componentAttributes",
                          "powerOutageCostStartDate",
                          null
                        );
                        return;
                      }

                      const clampedValue =
                        pickerDefaultStartDate && value < pickerDefaultStartDate
                          ? pickerDefaultStartDate
                          : value;

                      handleInputChange(
                        "componentAttributes",
                        "powerOutageCostStartDate",
                        toUtcWithSameClock(clampedValue)
                      );
                    }}
                    withSeconds={false}
                    clearable
                    valueFormat={`${projectDateFormat} HH:mm`}
                    minDate={pickerDefaultStartDate}
                    maxDate={pickerDefaultEndDate}
                  />
                </Grid.Col>
                <Grid.Col className="no-rep-end" {...responsiveCol} style={{ position: 'relative' }}>
                  <DateTimePicker
                    key={`outage-cost-end-${projectDateFormat}`}
                    {...getFieldAttributes("field-14")}
                    data-id="field-14"
                    label={t("outage.endDate.label")}
                    value={pickerCostEndDate || null}
                    onChange={(value) => {
                      setCostRowError("");
                      handleInputChange(
                        "componentAttributes",
                        "powerOutageCostEndDate",
                        toUtcWithSameClock(value)
                      );
                    }}
                    withSeconds={false}
                    clearable
                    valueFormat={`${projectDateFormat} HH:mm`}
                    minDate={pickerCostStartDate || pickerDefaultStartDate}
                    maxDate={pickerMaxEndDate}
                    disabled={!pickerCostStartDate}
                  />
                  {costRowError && (
                    <Text size="xs" color="red" style={{ position: 'absolute', bottom: -18, left: '2%' }}>
                      {costRowError}
                    </Text>
                  )}
                </Grid.Col>

            </Grid>
                      <div className="add-btn-wrapper" style={{ paddingTop: 15, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button
              aria-label="outage-add-unique"
              color="gray"
              variant="filled"
              onClick={handleAddUniqueOutageRow}
              loading={isAddingUniqueRow}
              disabled={outageType === "noRepetition"}
            >
              {t("outage.button.add")}
            </Button>
            <Button
              aria-label="outage-clear-unique"
              color="gray"
              variant="outline"
              onClick={handleClearUniqueOutageFields}
            >
              {t("common.reset")}
            </Button>
          </div>
                </>)}
                  <Grid gutter="md" className="mt-2 mb-2">
              <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            label={t("outage.powerOutageCost.label")}
            required
            value={
              form?.values?.componentAttributes?.gridParameters?.powerOutageCost || "150"
            }
            {...form.getInputProps("componentAttributes.gridParameters.powerOutageCost")}
            onChange={(e) => {
              handleInputChange("componentAttributes", "powerOutageCost", e.currentTarget.value);
            }}
            rightSection={
              <div
                style={{
                  padding: "4px",
                  marginRight: "1rem",
                }}
              >
                {projectDetails?.projectDTO?.currencyCode || "$"}/h
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
            </Grid>
      {outageType !== "fullYear" && rows?.length ? (
        <Grid gutter="md" className="mt-2 mb-2">
          <Grid.Col span={6}>
            <MantineReactTable table={tableData} />
          </Grid.Col>
          {rows?.length > 0 && (
            <Grid.Col span={6}>
              <div style={{
                padding: "0px",
                borderRadius: "4px",
                backgroundColor: isLightMode ? "#fff" : "var(--se-background-alternative)",
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column"
              }}>
                <h6 style={{
                  marginTop: "10px",
                  marginBottom: "0px",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: isLightMode ? "#333" : "#d5d7e0"
                }}>
                  {t("outage.chart.heading") || "Yearly Timeline"}
                </h6>
                <div style={{ flex: 1, width: "100%" }}>
                  <YearlyTimelineChart
                    events={convertTableFormatToChartFormat(rows)}
                    height={350}
                    isDarkMode={colorScheme === "dark"}
                    windowStartUtcMs={timelineStartUtcMs}
                    dateFormat={projectDateFormat}
                    maxDaysInYear={timelineMaxDays}
                  />
                </div>
              </div>
            </Grid.Col>
          )}
        </Grid>
      ) : null}
      <Text
        color={colorScheme === "dark" ? "#d5d7e0" : "black"}
        component="span"
        fw={700}
      >
        {t("outageManged")}
      </Text>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <Radio.Group
            value={outageOccurDurationType}
            {...form.getInputProps(
              "uiAttributes.gridParameters.outageOccurDuration.backup"
            )}
            onChange={(value) => handleOutageOccurDurationTypeChange(value)}
          >
            <Radio
              className="mt-2 mb-2"
              label={t("backup")}
              name="outage_occur_duration.full_backup"
              value="fullBackUp"
              {...getFieldAttributes("field-15")}
              data-id="field-15"
            />
            <Radio
              className="mt-2 mb-2"
              label={t("curtailedLoad")}
              id="curtailedLoadRadio"
              name="outage_occur_duration.full_backup"
              value="curtailedLoad"
              {...getFieldAttributes("field-16")}
              data-id="field-16"
            />
          </Radio.Group>
          <ProjectNumberInput
            {...getFieldAttributes("field-17")}
            data-id="field-17"
            className="mb-2"
            id="curtailedLoad"
            type="number"
            autoComplete="none"
            name="outage_occur_duration.curtailed_load_value"
            placeholder={t("outage.loadValue.placeholder")}
            maxLength="100"
            required
            value={
              form?.values?.uiAttributes?.gridParameters?.outageOccurDuration
                ?.backup === "fullBackUp"
                ? 100
                : form?.values?.uiAttributes?.gridParameters
                    ?.outageOccurDuration?.loadValue
            }
            {...form.getInputProps(
              "uiAttributes.gridParameters.outageOccurDuration.loadValue"
            )}
            onChange={(e) => {
              const trimmedValue = formatInputValue(e.currentTarget.value);
              handleInputChange(
                "uiAttributes",
                "outageOccurDuration.loadValue",
                trimmedValue
              );
            }}
            disabled={
              form?.values?.uiAttributes?.gridParameters?.outageOccurDuration
                ?.backup === "fullBackUp"
            }
          />
        </Grid.Col>
      </Grid>
    </div>
  );
});

OutageLoadManagement.propTypes = {
  form: PropTypes.object,
  handleInputChange: PropTypes.func,
  gridParameters: PropTypes.object,
};

export default OutageLoadManagement;